import React, { useEffect, useState } from "react";
import AppShell from "../components/layout/AppShell";
import { Card, Toggle, Spinner, EmptyState } from "../components/ui";
import { useToast } from "../context/ToastContext";
import { getNotificationPreferences, updateNotificationPreference } from "../api/notifications";
import { FiBell } from "react-icons/fi";

export default function NotificationSettings() {
  const { notify } = useToast();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null); // `${type}-${channel}` currently in flight

  useEffect(() => {
    getNotificationPreferences()
      .then((res) => setCategories(res.data?.categories || []))
      .catch(() => notify("Couldn't load notification settings.", "error"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = async (categoryKey, type, channel, nextValue) => {
    const key = `${type}-${channel}`;
    setSavingKey(key);

    // Optimistic update — settings pages should feel instant, and this is
    // the same pattern used everywhere else in the app for one-click
    // preference changes.
    const previous = categories;
    setCategories((prev) =>
      prev.map((cat) =>
        cat.key !== categoryKey
          ? cat
          : {
              ...cat,
              types: cat.types.map((t) => (t.type !== type ? t : { ...t, [channel]: nextValue })),
            }
      )
    );

    try {
      const current = categories.find((c) => c.key === categoryKey)?.types.find((t) => t.type === type);
      const inApp = channel === "inApp" ? nextValue : current?.inApp;
      const email = channel === "email" ? nextValue : current?.email;
      await updateNotificationPreference(type, inApp, email);
    } catch (e) {
      setCategories(previous); // roll back on failure
      notify("Couldn't save that setting. Please try again.", "error");
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <AppShell title="Notification settings" subtitle="Choose how you want to hear about activity in Empact Alumni">
      {loading ? (
        <div className="flex justify-center py-24">
          <Spinner className="h-8 w-8" />
        </div>
      ) : categories.length === 0 ? (
        <EmptyState icon={<FiBell className="h-6 w-6" />} title="Couldn't load settings" description="Please refresh the page and try again." />
      ) : (
        <div className="space-y-6">
          {categories.map((cat) => (
            <Card key={cat.key} className="overflow-hidden">
              <div className="border-b border-slate-100 px-5 py-3">
                <p className="text-sm font-semibold text-slate-900">{cat.label}</p>
              </div>
              <div className="divide-y divide-slate-100">
                <div className="flex items-center gap-4 px-5 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  <div className="flex-1" />
                  <div className="w-16 text-center">In-app</div>
                  <div className="w-16 text-center">Email</div>
                </div>
                {cat.types.map((t) => (
                  <div key={t.type} className="flex items-center gap-4 px-5 py-3.5">
                    <p className="flex-1 text-sm text-slate-700">{t.label}</p>
                    <div className="flex w-16 justify-center">
                      <Toggle
                        checked={t.inApp}
                        disabled={savingKey === `${t.type}-inApp`}
                        onChange={(val) => handleToggle(cat.key, t.type, "inApp", val)}
                        label={`In-app notifications for ${t.label}`}
                      />
                    </div>
                    <div className="flex w-16 justify-center">
                      <Toggle
                        checked={t.email}
                        disabled={savingKey === `${t.type}-email`}
                        onChange={(val) => handleToggle(cat.key, t.type, "email", val)}
                        label={`Email notifications for ${t.label}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </AppShell>
  );
}
